import { useMemo, useState } from 'react';

import { isDesktopV2 } from '@zola-helpers/client/dist/es';
import {
  getSearchedLocation,
  getSearchLocationForBriefLocation,
  vendorSearchUrl,
} from '@zola-helpers/client/dist/es/marketplace/vendorSearchUtils';
import {
  VENUES_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  BANDS_DJS_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
  SEARCHABLE_VENDOR_TAXONOMY_KEYS,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { getVendorWordPill } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { PencilIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Pencil';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';
import { Title2 } from '@zola/zola-ui/src/typography/Headings';

import cx from 'classnames';

import bakersTile from '~/assets/images/lp/tinified-mweb-bakers.jpg';
import beauticiansTile from '~/assets/images/lp/tinified-mweb-beauticians.jpg';
import beveragesTile from '~/assets/images/lp/tinified-mweb-beverages.jpg';
import caterersTile from '~/assets/images/lp/tinified-mweb-caterers.jpg';
import floristsTile from '~/assets/images/lp/tinified-mweb-florists.jpg';
import musiciansTile from '~/assets/images/lp/tinified-mweb-musicians.jpg';
import photographersTile from '~/assets/images/lp/tinified-mweb-photographers.jpg';
import plannersTile from '~/assets/images/lp/tinified-mweb-planners.jpg';
import venuesTile from '~/assets/images/lp/tinified-mweb-venues.jpg';
import videographersTile from '~/assets/images/lp/tinified-mweb-videographers.jpg';
import { useSearchedLocation } from '~/contexts/SearchContext';
import { useUserContext } from '~/contexts/UserContext';
import useWeddingLocation from '~/hooks/useWeddingLocation';
import { getBriefLocation } from '~/pages/couples/explore/util/helpers';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import experimentFlags from '~/util/experimentFlags';
import { isGuest } from '~/util/userContextUtils';
import { vendorCategoryLandingUrl } from '~/util/vendorSearchUrl';

import VendorIcon from '../common/icons/VendorIcon';

import styles from './vendorsByCategory.module.less';

export const ORDERED_VENDOR_CATEGORY_KEYS: SearchableVendorTaxonomyKey[] = [
  VENUES_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  BANDS_DJS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
];

type VendorsByCategoryProps = {
  className?: string;
  excludeCategoryByType?: VendorTaxonomyKey;
  showModal?: () => void;
  isClpTest?: boolean;
  /** Show image and hide icon at all breakpoints, not just mobile and tablet */
  isClpTestAllBreakpoints?: boolean;
  hideHed?: boolean;
  useLastSearchedLocation?: boolean;
};

type VendorsCategoryListProps = React.HTMLAttributes<HTMLDivElement> & {
  categories?: SearchableVendorTaxonomyKey[];
  openInNewTab?: boolean;
  onLinkClick?: () => void | null;
  isClpTest?: boolean;
  isClpTestAllBreakpoints?: boolean;
  useLastSearchedLocation?: boolean;
};

const mapVendorKeyToTile = (vendorTaxonomyKey: SearchableVendorTaxonomyKey) =>
  getVendorTaxonomySwitchFunc({
    [VENUES_TAXONOMY_KEY]: venuesTile,
    [PHOTOGRAPHERS_TAXONOMY_KEY]: photographersTile,
    [VIDEOGRAPHERS_TAXONOMY_KEY]: videographersTile,
    [FLORISTS_TAXONOMY_KEY]: floristsTile,
    [HAIR_MAKEUP_TAXONOMY_KEY]: beauticiansTile,
    [PLANNERS_TAXONOMY_KEY]: plannersTile,
    [BANDS_DJS_TAXONOMY_KEY]: musiciansTile,
    [CATERING_TAXONOMY_KEY]: caterersTile,
    [CAKES_DESSERTS_TAXONOMY_KEY]: bakersTile,
    [BAR_SERVICES_TAXONOMY_KEY]: beveragesTile,
  })(vendorTaxonomyKey);

export const VendorsCategoryList = ({
  categories = SEARCHABLE_VENDOR_TAXONOMY_KEYS,
  className,
  isClpTestAllBreakpoints = false,
  isClpTest = isClpTestAllBreakpoints,
  openInNewTab = false,
  onLinkClick = () => null,
  useLastSearchedLocation = false,
  ...rest
}: VendorsCategoryListProps): JSX.Element => {
  const userContext = useUserContext();
  const { weddingLocation } = useWeddingLocation();
  const { searchedLocation } = useSearchedLocation();

  const [lastSearchedLocation, setLastSearchedLocation] =
    useState<LocationOrAllMarketsType | null>();

  useEffectOnce(() => {
    setLastSearchedLocation(getSearchedLocation());
  });

  const variation =
    typeof window !== 'undefined' &&
    experimentFlags.getExperimentVariation(
      'marketplace-clp-lp-links-to-srp-v2',
      userContext,
      false
    );
  const isInSRPExperiment = useMemo(() => {
    return (
      !isGuest(userContext) &&
      typeof window !== 'undefined' &&
      isDesktopV2() &&
      (variation === 'TEST-1' || variation === 'TEST-2')
    );
  }, [userContext, variation]);

  const weddingLocationForSRP = useMemo(() => {
    return getSearchLocationForBriefLocation(getBriefLocation(weddingLocation));
  }, [weddingLocation]);

  const locationToUse = useMemo(() => {
    return isInSRPExperiment && useLastSearchedLocation
      ? lastSearchedLocation || searchedLocation || weddingLocationForSRP
      : searchedLocation;
  }, [
    isInSRPExperiment,
    useLastSearchedLocation,
    lastSearchedLocation,
    searchedLocation,
    weddingLocationForSRP,
  ]);

  const linkProps = openInNewTab
    ? {
        target: '_blank',
      }
    : {};

  return (
    <div
      className={cx(styles.categories, className, {
        [styles.gridAllBreakpoints]: isClpTestAllBreakpoints,
        [styles.grid]: isClpTest,
      })}
      {...rest}
      data-testid="category-links"
    >
      {categories.map((vendorTaxonomyKey) => {
        const url =
          isClpTest || isInSRPExperiment
            ? vendorSearchUrl(vendorTaxonomyKey, locationToUse?.slug)
            : vendorCategoryLandingUrl(vendorTaxonomyKey);
        const name = getVendorWordPill(vendorTaxonomyKey);
        return (
          <LinkV2
            className={cx(styles.category, {
              [styles.categoryAllBreakpoints]: isClpTestAllBreakpoints,
            })}
            key={`explore-by-category-${vendorTaxonomyKey}`}
            href={url}
            subtle={!isClpTest}
            noUnderline
            noTextTransform
            data-testid="category-link"
            onClick={() => {
              onLinkClick();
              if (useLastSearchedLocation) {
                experimentFlags.isUnderTest('marketplace-clp-lp-links-to-srp-v2', userContext);
              }
            }}
            {...linkProps}
          >
            {isClpTestAllBreakpoints ? null : (
              <VendorIcon
                className={cx(styles.icon, { [styles.hideNotDesktop]: isClpTest })}
                vendorTaxonomyKey={vendorTaxonomyKey}
                size={64}
              />
            )}
            {isClpTest && (
              <img
                className={cx(styles.tile, {
                  [styles.hideDesktop]: !isClpTestAllBreakpoints,
                })}
                src={mapVendorKeyToTile(vendorTaxonomyKey)}
                alt={name}
              />
            )}
            {name}
          </LinkV2>
        );
      })}
    </div>
  );
};

export const VendorsByCategory = ({
  className,
  excludeCategoryByType,
  isClpTestAllBreakpoints = false,
  isClpTest = isClpTestAllBreakpoints,
  hideHed = false,
  showModal,
  useLastSearchedLocation = false,
}: VendorsByCategoryProps) => {
  const { searchedLocation } = useSearchedLocation();
  const { isMobile, isTablet } = useResponsiveDesign();
  const filteredCategories = ORDERED_VENDOR_CATEGORY_KEYS.filter(
    (vendorTaxonomyKey) => excludeCategoryByType !== vendorTaxonomyKey
  );

  const hed =
    isClpTest && (isMobile || isTablet) ? (
      <Title2 className={styles.hed} presentation="h3">
        Find vendors in{' '}
        <LinkV2 role="button" onClick={showModal} subtle>
          {searchedLocation?.label} <PencilIcon height={20} width={20} />
        </LinkV2>
      </Title2>
    ) : (
      <Title2 className={styles.hed} presentation="h3">
        Explore wedding vendors by category
      </Title2>
    );

  return (
    <div className={cx(styles.vendorsByCategory, className)}>
      {!hideHed && hed}
      <VendorsCategoryList
        categories={filteredCategories}
        isClpTest={isClpTest}
        isClpTestAllBreakpoints={isClpTestAllBreakpoints}
        useLastSearchedLocation={useLastSearchedLocation}
      />
    </div>
  );
};

export default VendorsByCategory;
